import { useMemo, useState } from "react";
import {
  App,
  Button,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Category } from "@glow-price/shared";
import { ENTITY_STATUSES } from "@glow-price/shared";

import { categoriesApi } from "../api/resources";
import { CrudDrawer } from "../components/CrudDrawer";
import { PageHeader } from "../components";

const STATUS_OPTIONS = Object.values(ENTITY_STATUSES).map((v) => ({ value: v, label: v }));

type TreeCategory = Category & { children?: TreeCategory[] };

export function CategoriesPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [editing, setEditing] = useState<Category | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const list = useQuery({
    queryKey: ["categories"],
    queryFn: () => categoriesApi.list(),
  });

  const tree = useMemo<TreeCategory[]>(() => {
    const all = list.data?.items ?? [];
    const byId = new Map<string, TreeCategory>(all.map((c) => [c.id, { ...c, children: [] }]));
    const roots: TreeCategory[] = [];
    for (const c of byId.values()) {
      if (c.parentId && byId.has(c.parentId)) byId.get(c.parentId)!.children!.push(c);
      else roots.push(c);
    }
    const sortRec = (xs: TreeCategory[]) => {
      xs.sort((a, b) => a.sortOrder - b.sortOrder);
      xs.forEach((x) => x.children && sortRec(x.children));
    };
    sortRec(roots);
    return roots;
  }, [list.data]);

  const parentOptions = useMemo(
    () => [
      { value: null, label: "— 顶级分类 —" },
      ...(list.data?.items.filter((c) => c.parentId === null) ?? []).map((c) => ({
        value: c.id,
        label: c.nameCn,
      })),
    ],
    [list.data],
  );

  const saveMutation = useMutation({
    mutationFn: async (values: Partial<Category>) => {
      if (editing) return categoriesApi.update(editing.id, values);
      return categoriesApi.create(values);
    },
    onSuccess: () => {
      message.success("已保存");
      qc.invalidateQueries({ queryKey: ["categories"] });
      setDrawerOpen(false);
      setEditing(null);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoriesApi.remove(id),
    onSuccess: () => {
      message.success("已删除（关联商品的 category_id 已置空）");
      qc.invalidateQueries({ queryKey: ["categories"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  return (
    <div>
      <PageHeader
        title="分类管理"
        description="维护商品分类树，供标准商品归类与前台导航使用。"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
          >
            新建分类
          </Button>
        }
      />

      <Table<TreeCategory>
        rowKey="id"
        loading={list.isLoading}
        dataSource={tree}
        defaultExpandAllRows
        pagination={false}
        columns={[
          { title: "名称", dataIndex: "nameCn" },
          { title: "Slug", dataIndex: "slug", width: 200 },
          { title: "英文", dataIndex: "nameEn", width: 160 },
          { title: "排序", dataIndex: "sortOrder", width: 80 },
          { title: "状态", dataIndex: "status", width: 100, render: (v) => <Tag>{v}</Tag> },
          {
            title: "操作",
            width: 110,
            render: (_, row) => (
              <Space size={4}>
                <Tooltip title="编辑">
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => {
                      setEditing(row);
                      setDrawerOpen(true);
                    }}
                  />
                </Tooltip>
                <Popconfirm title="确认删除？" onConfirm={() => deleteMutation.mutate(row.id)}>
                  <Tooltip title="删除">
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                  </Tooltip>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <CrudDrawer<Partial<Category>>
        open={drawerOpen}
        title={editing ? "编辑分类" : "新建分类"}
        initialValues={
          editing ?? {
            parentId: null,
            sortOrder: 0,
            status: ENTITY_STATUSES.ACTIVE,
          }
        }
        loading={saveMutation.isPending}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        onSubmit={(values) => saveMutation.mutateAsync(values)}
      >
        {() => (
          <>
            <Form.Item name="parentId" label="父分类">
              <Select options={parentOptions as never} allowClear />
            </Form.Item>
            <Form.Item name="slug" label="Slug" rules={[{ required: true }]}>
              <Input placeholder="skincare / lipstick ..." />
            </Form.Item>
            <Form.Item name="nameCn" label="中文名" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="nameEn" label="英文名">
              <Input />
            </Form.Item>
            <Form.Item name="iconUrl" label="图标 URL">
              <Input placeholder="https://..." />
            </Form.Item>
            <Form.Item name="sortOrder" label="排序（越小越靠前）">
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
          </>
        )}
      </CrudDrawer>
    </div>
  );
}
